import { zodResolver } from "@hookform/resolvers/zod";
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import {
  useConfirmResetPassword,
  useResetPassword,
  useSignIn,
} from "@theprelude/core";
import {
  Button,
  Form,
  InputFormField,
  notifyError,
  notifySuccess,
  PasswordFormField,
  RefreshIcon,
} from "@theprelude/ds";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import AuthPage from "@/components/auth-page";
import { passwordSchema } from "@/lib/validators";

const signInSchema = z.object({
  redirect: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/sign-in/forgot")({
  beforeLoad: async ({ context }) => {
    const authConfig = await context.auth.getAuthConfig();

    if (authConfig.status === "authenticated") {
      return redirect({ to: "/home" });
    }
  },
  validateSearch: signInSchema,
  component: ForgotSignInPage,
});

const forgotSignInSchema = z.object({
  email: z.string().email(),
});

type ForgotSignInForm = z.infer<typeof forgotSignInSchema>;

function ForgotSignInPage() {
  const [tokenEmail, setTokenEmail] = useState<string | undefined>(undefined);
  const resetPassword = useResetPassword();
  const form = useForm<ForgotSignInForm>({
    resolver: zodResolver(forgotSignInSchema),
  });
  const { register } = form;

  const submit = (data: ForgotSignInForm) => {
    resetPassword.mutate(
      { ...data },
      {
        onSettled: () => {
          notifySuccess({
            message: `Please follow instructions sent to ${data.email}`,
          });
          setTokenEmail(data.email);
        },
      },
    );
  };

  return (
    <AuthPage title="Forgot your password?">
      {tokenEmail ? (
        <TokenView email={tokenEmail} />
      ) : (
        <>
          <span className="text-base">
            Enter your email address and we&apos;ll send you a temporary
            confirmation code to reset your password.
          </span>
          <main>
            <Form
              {...form}
              onSubmitValid={submit}
              className="flex w-full flex-col gap-4"
            >
              <InputFormField
                label="Email"
                {...register("email")}
                placeholder={"Required"}
                required
                type="email"
              />

              <div className="flex flex-col items-start justify-between gap-4 pt-4">
                <Button
                  loading={resetPassword.isPending}
                  disabled={resetPassword.isPending}
                  intent="primary"
                  type="submit"
                  className="flex w-full justify-center"
                >
                  Reset Password
                </Button>
                <div className="flex flex-col items-end gap-2">
                  <Link to="/sign-in" className="underline underline-offset-2">
                    Go back
                  </Link>
                </div>
              </div>
            </Form>
          </main>
        </>
      )}
    </AuthPage>
  );
}

const resetPasswordSchema = z
  .object({
    email: z.string().email(),
    token: z.string(),
    password: passwordSchema,
    confirmPassword: z.string().min(1),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

const TokenView: React.FC<{
  email: string;
}> = ({ email }) => {
  const navigate = useNavigate();
  const signIn = useSignIn();
  const confirmResetPassword = useConfirmResetPassword();
  const resetPassword = useResetPassword();

  const form = useForm<ResetPasswordForm>({
    defaultValues: { email },
    resolver: zodResolver(resetPasswordSchema),
  });

  const { register } = form;

  const submit = async (data: ResetPasswordForm) => {
    await confirmResetPassword.mutateAsync(
      { email: data.email, code: data.token, password: data.password },
      {
        onError: () => {
          notifyError({
            message: "Failed to reset password.",
          });
        },
      },
    );

    await signIn.mutateAsync(
      {
        email: data.email,
        password: data.password,
      },
      {
        onSuccess: () => {
          void navigate({
            to: "/home",
          });
        },
        onError: () => {
          notifyError({
            message: "Failed to sign in. Please check your credentials.",
          });
        },
      },
    );
  };

  return (
    <>
      <span className="text-base">
        Please check your email <b>{email}</b> for an email from Prelude and use
        the temporary confirmation code to sign in to your account.
      </span>
      <main>
        <Form {...form} onSubmitValid={submit} className="flex flex-col gap-4">
          <InputFormField
            label="Confirmation code"
            {...register("token")}
            placeholder="Enter the received code"
            required
          />

          <PasswordFormField
            {...register("password")}
            label="New Password"
            placeholder="Enter your new password"
            required
          />

          <PasswordFormField
            {...register("confirmPassword")}
            label="Confirm Password"
            placeholder="Confirm your new password"
            required
          />

          <div className="flex flex-col justify-between gap-4 pt-4">
            <Button
              className="flex w-full justify-center"
              loading={signIn.isPending || confirmResetPassword.isPending}
              disabled={signIn.isPending || confirmResetPassword.isPending}
              intent="primary"
              type="submit"
            >
              Sign in
            </Button>
            <Button
              icon={<RefreshIcon />}
              intent="secondary"
              className="flex w-full justify-center"
              type="button"
              loading={resetPassword.isPending}
              disabled={resetPassword.isPending}
              onClick={() => {
                resetPassword.mutate(
                  { email: email },
                  {
                    onSuccess: () => {
                      notifySuccess({
                        message: `Please follow instructions sent to ${email}`,
                      });
                    },
                    onError: () => {
                      notifyError({
                        message: "Failed to resend email.",
                      });
                    },
                  },
                );
              }}
            >
              Resend email
            </Button>
          </div>
        </Form>
      </main>
    </>
  );
};
